import type { StageInfo } from '../stages/StageData';
import { getLocalizedStage } from '../stages/StageData';
import { sounds } from '../audio/SoundEffects';
import { i18n } from '../i18n/I18nService';
import { themeManager } from '../engine/ThemeManager';

export class HUDController {
  private stageNumEl: HTMLElement;
  private stageNameEl: HTMLElement;
  private accuracyFillEl: HTMLElement;
  private accuracyValEl: HTMLElement;
  private finishFillEl: HTMLElement;
  private finishValEl: HTMLElement;
  private timerEl: HTMLElement;
  private coinValEl: HTMLElement;
  private soundBtn: HTMLButtonElement;
  private iconSoundOn: SVGElement;
  private iconSoundOff: SVGElement;
  private targetColorsListEl: HTMLElement;
  private targetPreviewCanvas: HTMLCanvasElement;
  private themeToggleBtn: HTMLButtonElement;
  private themeIconEl: HTMLElement;
  private langToggleBtn: HTMLButtonElement;
  private tutorialBtn: HTMLButtonElement;
  private shopBtn: HTMLButtonElement;
  private currentStage?: StageInfo;

  private onFinishCallback?: () => void;
  private onMenuCallback?: () => void;
  private onTutorialCallback?: () => void;
  private onShopCallback?: () => void;
  private onBlueprintCallback?: (stage: StageInfo) => void;

  constructor() {
    this.stageNumEl = document.getElementById('hud-stage-num')!;
    this.stageNameEl = document.getElementById('hud-stage-name')!;
    this.accuracyFillEl = document.getElementById('hud-accuracy-fill')!;
    this.accuracyValEl = document.getElementById('hud-accuracy-val')!;
    this.finishFillEl = document.getElementById('hud-finish-fill')!;
    this.finishValEl = document.getElementById('hud-finish-val')!;
    this.timerEl = document.getElementById('hud-timer')!;
    this.coinValEl = document.getElementById('hud-coin-val')!;
    this.soundBtn = document.getElementById('btn-sound-toggle') as HTMLButtonElement;
    this.iconSoundOn = document.getElementById('icon-sound-on') as unknown as SVGElement;
    this.iconSoundOff = document.getElementById('icon-sound-off') as unknown as SVGElement;
    this.targetColorsListEl = document.getElementById('target-colors-list')!;
    this.targetPreviewCanvas = document.getElementById('target-preview-canvas') as HTMLCanvasElement;
    this.themeToggleBtn = document.getElementById('btn-hud-theme') as HTMLButtonElement;
    this.themeIconEl = document.getElementById('theme-hud-icon')!;
    this.langToggleBtn = document.getElementById('btn-hud-lang') as HTMLButtonElement;
    this.tutorialBtn = document.getElementById('btn-hud-tutorial') as HTMLButtonElement;
    this.shopBtn = document.getElementById('btn-hud-shop') as HTMLButtonElement;

    this.initEvents();
    this.updateLanguageUI();
    this.updateThemeUI();

    i18n.subscribe(() => {
      this.updateLanguageUI();
      if (this.currentStage) {
        this.setStage(this.currentStage);
      }
    });

    themeManager.subscribe(() => {
      this.updateThemeUI();
    });
  }

  private updateThemeUI() {
    const isDark = themeManager.getTheme() === 'dark';
    if (this.themeIconEl) {
      this.themeIconEl.textContent = isDark ? '☀️' : '🌙';
    }
  }

  private initEvents() {
    this.soundBtn.addEventListener('click', () => {
      const enabled = sounds.toggleSound();
      if (enabled) {
        this.iconSoundOn.classList.remove('hidden');
        this.iconSoundOff.classList.add('hidden');
      } else {
        this.iconSoundOn.classList.add('hidden');
        this.iconSoundOff.classList.remove('hidden');
      }
    });

    this.themeToggleBtn?.addEventListener('click', () => {
      sounds.playClick();
      themeManager.toggleTheme();
    });

    document.getElementById('btn-finish-work')?.addEventListener('click', () => {
      sounds.playClick();
      if (this.onFinishCallback) this.onFinishCallback();
    });

    document.getElementById('btn-back-menu')?.addEventListener('click', () => {
      sounds.playClick();
      if (this.onMenuCallback) this.onMenuCallback();
    });

    this.langToggleBtn?.addEventListener('click', () => {
      sounds.playClick();
      i18n.toggleLanguage();
    });

    this.tutorialBtn?.addEventListener('click', () => {
      sounds.playClick();
      if (this.onTutorialCallback) this.onTutorialCallback();
    });

    this.shopBtn?.addEventListener('click', () => {
      sounds.playClick();
      if (this.onShopCallback) this.onShopCallback();
    });

    const triggerBlueprint = () => {
      if (this.currentStage && this.onBlueprintCallback) {
        sounds.playClick();
        this.onBlueprintCallback(this.currentStage);
      }
    };

    document.getElementById('btn-open-blueprint')?.addEventListener('click', triggerBlueprint);
    document.getElementById('target-preview-clickable')?.addEventListener('click', triggerBlueprint);

    // Spec Card Minimization / Collapse
    const specCardEl = document.getElementById('target-spec-card')!;
    const btnToggleSpec = document.getElementById('btn-toggle-spec')!;
    const btnSpecMinimized = document.getElementById('btn-spec-minimized')!;
    const specCardHeader = document.getElementById('spec-card-header')!;

    const minimizeSpecCard = () => {
      sounds.playClick();
      specCardEl.classList.add('collapsed');
      btnSpecMinimized.classList.remove('hidden');
    };

    const expandSpecCard = () => {
      sounds.playClick();
      specCardEl.classList.remove('collapsed');
      btnSpecMinimized.classList.add('hidden');
    };

    btnToggleSpec?.addEventListener('click', minimizeSpecCard);
    btnSpecMinimized?.addEventListener('click', expandSpecCard);

    // Spec Card Touch/Pointer Dragging
    let isDraggingSpec = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let initialLeft = 0;
    let initialTop = 0;

    specCardHeader?.addEventListener('pointerdown', (e) => {
      // Don't drag if clicking action buttons inside header
      if ((e.target as HTMLElement).closest('.spec-header-actions')) return;
      if (e.button !== 0 && e.pointerType === 'mouse') return;

      isDraggingSpec = true;
      specCardHeader.setPointerCapture(e.pointerId);
      dragStartX = e.clientX;
      dragStartY = e.clientY;

      const rect = specCardEl.getBoundingClientRect();
      initialLeft = rect.left;
      initialTop = rect.top;
      specCardHeader.style.cursor = 'grabbing';
    });

    specCardHeader?.addEventListener('pointermove', (e) => {
      if (!isDraggingSpec) return;
      const dx = e.clientX - dragStartX;
      const dy = e.clientY - dragStartY;

      const cardWidth = specCardEl.offsetWidth;
      const cardHeight = specCardEl.offsetHeight;
      const maxLeft = window.innerWidth - cardWidth - 8;
      const maxTop = window.innerHeight - cardHeight - 80;

      const newLeft = Math.max(8, Math.min(maxLeft, initialLeft + dx));
      const newTop = Math.max(54, Math.min(maxTop, initialTop + dy));

      specCardEl.style.left = `${newLeft}px`;
      specCardEl.style.top = `${newTop}px`;
      specCardEl.style.right = 'auto';
      specCardEl.style.bottom = 'auto';
    });

    const endSpecDrag = (e: PointerEvent) => {
      if (isDraggingSpec) {
        if (specCardHeader.hasPointerCapture(e.pointerId)) {
          specCardHeader.releasePointerCapture(e.pointerId);
        }
        isDraggingSpec = false;
        specCardHeader.style.cursor = 'grab';
      }
    };

    specCardHeader?.addEventListener('pointerup', endSpecDrag);
    specCardHeader?.addEventListener('pointercancel', endSpecDrag);
  }

  public setCallbacks(
    onFinish: () => void,
    onMenu: () => void,
    onTutorial: () => void,
    onShop?: () => void,
    onBlueprint?: (stage: StageInfo) => void
  ) {
    this.onFinishCallback = onFinish;
    this.onMenuCallback = onMenu;
    this.onTutorialCallback = onTutorial;
    this.onShopCallback = onShop;
    this.onBlueprintCallback = onBlueprint;
  }

  public setStage(stage: StageInfo) {
    this.currentStage = stage;
    const localized = getLocalizedStage(stage, i18n.getLanguage());

    this.stageNumEl.textContent = `STAGE ${stage.id}`;
    this.stageNameEl.textContent = localized.name;

    // Draw Target Spec Canvas Preview (Clean centered high-dpi scaling)
    const ctx = this.targetPreviewCanvas.getContext('2d')!;
    const pw = this.targetPreviewCanvas.width;
    const ph = this.targetPreviewCanvas.height;
    ctx.clearRect(0, 0, pw, ph);
    ctx.save();
    const scale = Math.min(pw / 840, ph / 920) * 0.95;
    const ox = (pw - 840 * scale) / 2;
    const oy = (ph - 920 * scale) / 2;
    ctx.translate(ox, oy);
    ctx.scale(scale, scale);
    stage.drawObject(ctx, 840, 920, 'base');
    stage.drawObject(ctx, 840, 920, 'targetPreview');
    stage.drawObject(ctx, 840, 920, 'outline');
    ctx.restore();

    // Populate Target Colors List
    this.targetColorsListEl.innerHTML = '';
    localized.targetColors.forEach(tc => {
      const item = document.createElement('div');
      item.className = 'target-color-item';
      item.innerHTML = `
        <span class="target-color-swatch" style="background: ${tc.hex};"></span>
        <span class="target-color-name">${tc.name}</span>
      `;
      this.targetColorsListEl.appendChild(item);
    });
  }

  public updateMetrics(accuracy: number, finish: number) {
    this.accuracyFillEl.style.width = `${accuracy}%`;
    this.accuracyValEl.textContent = `${accuracy}%`;

    this.finishFillEl.style.width = `${finish}%`;
    this.finishValEl.textContent = `${finish}%`;
  }

  public updateCoins(coins: number) {
    if (this.coinValEl) {
      this.coinValEl.textContent = coins.toLocaleString();
    }
  }

  public updateTimer(remainingSeconds: number) {
    const mins = Math.floor(remainingSeconds / 60);
    const secs = remainingSeconds % 60;
    this.timerEl.textContent = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

    if (remainingSeconds <= 10) {
      this.timerEl.parentElement?.classList.add('timer-warning');
    } else {
      this.timerEl.parentElement?.classList.remove('timer-warning');
    }
  }

  public updateLanguageUI() {
    const t = i18n.dict.hud;
    if (this.langToggleBtn) {
      this.langToggleBtn.innerHTML = i18n.getLanguage() === 'ja' ? '🇯🇵 JP' : '🇺🇸 EN';
    }

    const accuracyLabelEl = document.querySelector('.accuracy-chip .chip-label');
    if (accuracyLabelEl) accuracyLabelEl.textContent = t.accuracyLabel;

    const finishLabelEl = document.querySelector('.finish-chip .chip-label');
    if (finishLabelEl) finishLabelEl.textContent = t.finishLabel;

    const specBadgeEl = document.querySelector('.spec-badge');
    if (specBadgeEl) specBadgeEl.textContent = t.specTitle;

    const finishBtnSpan = document.querySelector('#btn-finish-work span');
    if (finishBtnSpan) finishBtnSpan.textContent = t.finishBtn;

    const tutorialBtnSpan = document.querySelector('#btn-hud-tutorial .btn-text');
    if (tutorialBtnSpan) tutorialBtnSpan.textContent = t.tutorialBtn;

    const shopBtnSpan = document.querySelector('#btn-hud-shop .btn-text');
    if (shopBtnSpan) shopBtnSpan.textContent = i18n.getLanguage() === 'ja' ? 'ショップ' : 'Shop';
  }
}
